
import React from "react";
import { CheckCircle, User, Award, Star, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardDescription, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AIStaffMember } from "@/lib/types";

interface OnboardingStageProps {
  aiStaff: AIStaffMember | null;
  onComplete: () => void;
}

const OnboardingStage: React.FC<OnboardingStageProps> = ({ aiStaff, onComplete }) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        <div className="text-center mb-8">
          <Trophy className="h-16 w-16 text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold">Congratulations!</h3>
          <p className="text-muted-foreground">
            You've successfully hired your AI Marketing Assistant. Let's get started with the onboarding process.
          </p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Welcome Your New Marketing Assistant</CardTitle>
            <CardDescription>
              {aiStaff?.name || "Your AI Assistant"} is ready to help you with your marketing needs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="tools" className="w-full">
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="tools">Available Tools</TabsTrigger>
                <TabsTrigger value="levels">Growth Path</TabsTrigger>
                <TabsTrigger value="next">Next Steps</TabsTrigger>
              </TabsList>
              
              <TabsContent value="tools" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Social Media Creator</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Generate engaging posts for multiple platforms with just a few clicks.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Email Campaign Builder</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Create professional email campaigns that convert leads into customers.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Content Calendar</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Plan and schedule your content across multiple channels.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Analytics Dashboard</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Track the performance of your marketing campaigns in real-time.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="levels" className="space-y-4">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-3 ${aiStaff?.level === 'intern' ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <User className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Intern Level</h4>
                      <p className="text-sm text-muted-foreground">Basic social media capabilities</p>
                    </div>
                    {aiStaff?.level === 'intern' && <Badge>Current</Badge>}
                  </div>
                  
                  <div className="w-[1px] h-6 bg-muted mx-auto"></div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-3 ${aiStaff?.level === 'junior' ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <Award className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Junior Level</h4>
                      <p className="text-sm text-muted-foreground">Advanced content creation and scheduling</p>
                    </div>
                    {aiStaff?.level === 'junior' && <Badge>Current</Badge>}
                  </div>
                  
                  <div className="w-[1px] h-6 bg-muted mx-auto"></div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-3 ${aiStaff?.level === 'senior' ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <Star className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Senior Level</h4>
                      <p className="text-sm text-muted-foreground">Full marketing suite with email and ads</p>
                    </div>
                    {aiStaff?.level === 'senior' && <Badge>Current</Badge>}
                  </div>
                  
                  <div className="w-[1px] h-6 bg-muted mx-auto"></div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-3 ${aiStaff?.level === 'director' ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <Trophy className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Director Level</h4>
                      <p className="text-sm text-muted-foreground">Advanced analytics and AI-powered strategy</p>
                    </div>
                    {aiStaff?.level === 'director' && <Badge>Current</Badge>}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="next" className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Create Your First Social Media Post</h4>
                      <p className="text-sm text-muted-foreground">
                        Start by creating a social media post to announce your new product or service.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Set Up Your Content Calendar</h4>
                      <p className="text-sm text-muted-foreground">
                        Plan your content schedule for the next week to maximize engagement.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Track Your First Campaign</h4>
                      <p className="text-sm text-muted-foreground">
                        Monitor the performance of your marketing efforts to see what's working.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter>
            <Button onClick={onComplete} className="w-full">
              Get Started with Your Marketing Assistant
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingStage;
